import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import f1Service from '../../../components/dashboards/services/f1Service';

/**
 * F1 Constructor Championship Battle
 * Visual bar chart showing team standings with team colors
 * Displays championship battle with animated bars
 */
const F1ConstructorBattle = ({ season = 2025, maxTeams = 10 }) => {
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
 
  useEffect(() => {
    const fetchStandings = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await f1Service.getConstructorStandings(season);

        if (response && response.response) {
          // Limit to top N teams
          setStandings(response.response.slice(0, maxTeams));
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching constructor standings:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchStandings();
  }, [season, maxTeams]);

  // Get team colors
  const getTeamColor = (teamName) => {
    const colors = f1Service.getTeamColors(teamName);
    return colors.primary;
  };

  // Calculate max points for bar width calculation
  const maxPoints = standings.length > 0 ? standings[0].points : 0;

  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
        <div className="p-4 border-b border-white/5">
          <div className="h-4 w-48 bg-white/10 rounded animate-pulse" />
        </div>
        <div className="p-4 space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="flex justify-between">
                <div className="h-3 w-24 bg-white/10 rounded animate-pulse" />
                <div className="h-3 w-12 bg-white/10 rounded animate-pulse" />
              </div>
              <div className="h-6 bg-white/10 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white/[0.02] border border-red-500/20 rounded-sm p-4">
        <p className="text-xs text-red-400 font-mono">Error: {error}</p>
      </div>
    );
  }

  // Empty state
  if (standings.length === 0) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-sm p-8 text-center">
        <p className="text-xs text-gray-600 font-mono uppercase tracking-wider">
          No Constructor Data Available
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider font-mono">
            Constructor Championship
          </h3>
          <span className="text-[10px] text-gray-600 font-mono uppercase tracking-wider">
            {season}
          </span>
        </div>
      </div>

      {/* Battle Chart */}
      <div className="p-4 space-y-4">
        {standings.map((standing, index) => {
          const teamColor = getTeamColor(standing.team?.name);
          const barWidth = maxPoints > 0 ? (standing.points / maxPoints) * 100 : 0;
          const isLeader = standing.position === 1;
          const teamCode = f1Service.getTeamCode(standing.team?.name);

          return (
            <motion.div
              key={standing.team?.id || index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05, duration: 0.3 }}
              className="space-y-2"
            >
              {/* Team Info Row */}
              <div className="flex items-center justify-between text-xs font-mono">
                <div className="flex items-center gap-2">
                  {/* Position */}
                  <span
                    className={`font-bold tabular-nums w-6 ${
                      isLeader ? 'text-[#E10600]' : 'text-gray-500'
                    }`}
                  >
                    {standing.position}
                  </span>
                  {/* Team Name */}
                  <div className="flex items-center gap-2">
                    <div
                      className="w-1 h-5 rounded-full"
                      style={{ backgroundColor: teamColor }}
                    />
                    <span className="text-white font-semibold">
                      {teamCode}
                    </span>
                  </div>
                </div>

                {/* Points */}
                <div className="flex items-center gap-3">
                  <span className="text-white font-bold tabular-nums">
                    {standing.points}
                  </span>
                  <span className="text-gray-600 uppercase tracking-wider text-[10px]">
                    PTS
                  </span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="relative h-12 bg-white/[0.02] rounded-sm overflow-hidden">
                {/* Bar Fill */}
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${barWidth}%` }}
                  transition={{ delay: index * 0.05 + 0.2, duration: 0.6, ease: 'easeOut' }}
                  className="absolute inset-y-0 left-0 rounded-sm"
                  style={{
                    backgroundColor: teamColor,
                    opacity: isLeader ? 0.3 : 0.2
                  }}
                />

                {/* Bar Border/Accent */}
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${barWidth}%` }}
                  transition={{ delay: index * 0.05 + 0.2, duration: 0.6, ease: 'easeOut' }}
                  className="absolute inset-y-0 left-0 border-r-2"
                  style={{ borderColor: teamColor }}
                />

                {/* Car Image Inside Bar */}
                {f1Service.getTeamCarImage(standing.team?.name, season) && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.05 + 0.5, duration: 0.4 }}
                    className="absolute inset-y-0 right-2 flex items-center"
                  >
                    <img
                      src={f1Service.getTeamCarImage(standing.team?.name, season)}
                      alt={`${standing.team?.name} Car`}
                      className="h-10 w-auto object-contain opacity-30"
                    />
                  </motion.div>
                )}

                {/* Team Name Inside Bar */}
                <div className="absolute inset-0 flex items-center px-3">
                  <span
                    className="text-[10px] font-bold uppercase tracking-wider z-10"
                    style={{ color: teamColor }}
                  >
                    {standing.team?.name}
                  </span>
                </div>

                {/* Champion Badge */}
                {isLeader && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <div className="px-2 py-0.5 bg-[#E10600] rounded text-[9px] font-bold text-white uppercase tracking-wider">
                      Leader
                    </div>
                  </div>
                )}
              </div>

              {/* Gap to Leader (if not leader) */}
              {!isLeader && maxPoints > 0 && (
                <div className="flex justify-end">
                  <span className="text-[10px] text-gray-600 font-mono">
                    -{maxPoints - standing.points} pts
                  </span>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-white/5 bg-white/[0.01]">
        <div className="flex items-center justify-between text-[10px] text-gray-600 font-mono uppercase tracking-wider">
          <span>Constructors</span>
          <span className="text-white font-bold">{standings.length}</span>
        </div>
      </div>
    </div>
  );
};

export default F1ConstructorBattle;
